<template>
  <div>
    <div class="mb-4">
      <h2 class="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-4">
        Synchronisatie
      </h2>
    </div>

    <!-- Sync Status Card -->
    <div
      class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 sm:p-6 mb-4"
    >
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3">
          <!-- Status indicator -->
          <span v-if="syncStatusData?.active" class="relative flex h-3 w-3">
            <span
              class="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-400 opacity-75"
            ></span>
            <span class="relative inline-flex rounded-full h-3 w-3 bg-yellow-500"></span>
          </span>
          <span v-else class="relative flex h-3 w-3">
            <span class="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
          </span>

          <div>
            <h3 class="text-sm font-semibold text-gray-900 dark:text-white">Sync Status</h3>
            <p
              v-if="syncStatusData?.active && syncStatusData.syncs?.length"
              class="text-xs text-gray-500 dark:text-gray-400 mt-0.5"
            >
              {{ syncStatusSummary }}
            </p>
            <p v-else class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Geen actieve synchronisatie
            </p>
          </div>
        </div>

        <button
          v-if="syncStatusData?.active"
          @click="handleForceUnlock"
          :disabled="isForceUnlocking"
          class="bg-red-600 hover:bg-red-700 disabled:bg-red-400 disabled:cursor-not-allowed text-white px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-colors text-xs font-medium"
        >
          <LockOpenIcon class="h-3.5 w-3.5" />
          {{ isForceUnlocking ? 'Ontgrendelen...' : 'Forceer ontgrendeling' }}
        </button>
      </div>

      <!-- Active sync details -->
      <div v-if="syncStatusData?.active && syncStatusData.syncs?.length" class="mt-3 space-y-2">
        <div
          v-for="sync in syncStatusData.syncs"
          :key="sync.id"
          class="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md px-3 py-2"
        >
          <div class="flex items-center justify-between text-xs">
            <span class="font-medium text-yellow-800 dark:text-yellow-300">
              {{ getSyncTypeName(sync.type) }}
            </span>
            <span class="text-yellow-600 dark:text-yellow-400">
              {{ formatSyncDuration(sync.started_at) }}
            </span>
          </div>
          <div
            v-if="sync.step && sync.step !== 'starting'"
            class="text-xs text-yellow-700 dark:text-yellow-400 mt-1"
          >
            Stap: {{ sync.step }}
            <span v-if="sync.total > 0"> — {{ sync.processed }}/{{ sync.total }}</span>
          </div>
          <div
            v-if="sync.message"
            class="text-xs text-yellow-600 dark:text-yellow-500 mt-0.5 truncate"
          >
            {{ sync.message }}
          </div>
        </div>
      </div>
    </div>

    <!-- Sync result notification -->
    <SyncResultNotification
      v-if="fullSyncResult"
      :result="fullSyncResult"
      @close="fullSyncResult = null"
    />

    <!-- Full Employee Sync Card -->
    <div
      class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 sm:p-6"
    >
      <h3 class="text-sm font-semibold text-gray-900 dark:text-white mb-2">
        Volledige medewerker sync
      </h3>
      <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">
        Haalt alle medewerkers op uit Simplicate, inclusief ex-medewerkers (uit dienst). Hierdoor
        worden medewerkers die bij de normale sync gemist worden correct bijgewerkt. Ex-medewerkers
        blijven geanonimiseerd (GDPR). Dit proces duurt langer dan een normale sync omdat er meer
        medewerkers worden opgehaald.
      </p>
      <div class="relative inline-block">
        <span
          v-if="employeeSyncLabel"
          :title="employeeSyncFull"
          class="absolute bottom-full left-0 mb-0.5 text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap"
        >
          {{ employeeSyncLabel }}
        </span>
        <button
          @click="startFullEmployeeSync()"
          :disabled="isFullSyncing"
          class="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors text-sm"
        >
          <ArrowPathIcon class="h-4 w-4" :class="{ 'animate-spin': isFullSyncing }" />
          Volledige medewerker sync
        </button>
      </div>
    </div>

    <!-- Sync Progress Modal -->
    <SyncProgressModal
      :is-open="isFullSyncModalOpen"
      sync-type="employees-all"
      :progress="fullSyncProgress"
      :has-error="fullSyncHasError"
      :error-message="fullSyncErrorMessage"
      @close="closeFullSyncModal"
      @retry="retryFullSync"
    />

    <!-- Timeline Prospects Sync Card -->
    <SyncResultNotification
      v-if="timelineSyncResult"
      :result="timelineSyncResult"
      @close="timelineSyncResult = null"
    />

    <div
      class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 sm:p-6 mt-4"
    >
      <h3 class="text-sm font-semibold text-gray-900 dark:text-white mb-2">
        Timeline verrijken (open deals)
      </h3>
      <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">
        Haalt timeline-data op uit Simplicate voor alle open prospects/leads. Dit voegt
        fase-overgangen, offerte-data en berichtentijdstippen toe, nodig voor het 15-feature
        ML-model. Alleen open deals worden verrijkt (geen historische data).
      </p>
      <div class="relative inline-block">
        <span
          v-if="timelineSyncLabel"
          :title="timelineSyncFull"
          class="absolute bottom-full left-0 mb-0.5 text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap"
        >
          {{ timelineSyncLabel }}
        </span>
        <button
          @click="startTimelineSync()"
          :disabled="isTimelineSyncing"
          class="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors text-sm"
        >
          <ClockIcon class="h-4 w-4" />
          Timeline verrijken
        </button>
      </div>
    </div>

    <!-- Timeline Sync Progress Modal -->
    <SyncProgressModal
      :is-open="isTimelineSyncModalOpen"
      sync-type="timeline-prospects"
      :progress="timelineSyncProgress"
      :has-error="timelineSyncHasError"
      :error-message="timelineSyncErrorMessage"
      @close="closeTimelineSyncModal"
      @retry="retryTimelineSync"
    />

    <!-- Services & Invoices Sync Card -->
    <SyncResultNotification
      v-if="servicesSyncResult"
      :result="servicesSyncResult"
      @close="servicesSyncResult = null"
    />

    <div
      class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 sm:p-6 mt-4"
    >
      <h3 class="text-sm font-semibold text-gray-900 dark:text-white mb-2">
        Diensten & facturen sync
      </h3>
      <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">
        Haalt alle vaste-prijs diensten en facturen op uit Simplicate. Dit is nodig voor de "Derden
        omzet" berekening in het Realisatie en Prognose dashboard.
      </p>
      <div class="relative inline-block">
        <span
          v-if="servicesSyncLabel"
          :title="servicesSyncFull"
          class="absolute bottom-full left-0 mb-0.5 text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap"
        >
          {{ servicesSyncLabel }}
        </span>
        <button
          @click="startServicesSync()"
          :disabled="isServicesSyncing"
          class="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors text-sm"
        >
          <ArrowPathIcon class="h-4 w-4" :class="{ 'animate-spin': isServicesSyncing }" />
          Diensten & facturen sync
        </button>
      </div>
    </div>

    <!-- Services Sync Progress Modal -->
    <SyncProgressModal
      :is-open="isServicesSyncModalOpen"
      sync-type="services"
      :progress="servicesSyncProgress"
      :has-error="servicesSyncHasError"
      :error-message="servicesSyncErrorMessage"
      @close="closeServicesSyncModal"
      @retry="retryServicesSync"
    />

    <!-- Force unlock confirmation modal -->
    <ConfirmActionModal
      :open="showForceUnlockConfirm"
      title="Synchronisaties ontgrendelen"
      message="Weet je zeker dat je alle actieve synchronisaties wilt ontgrendelen? Dit markeert ze als mislukt."
      confirm-label="Ontgrendelen"
      variant="warning"
      @confirm="confirmForceUnlock"
      @cancel="showForceUnlockConfirm = false"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { ArrowPathIcon, ClockIcon, LockOpenIcon } from '@heroicons/vue/24/outline'
import { servicesService } from '@/services/adminService.js'
import { employeeService } from '@/services/employeeService.js'
import { prospectsLeadsService } from '@/services/prospectService.js'
import { syncService } from '@/services/syncService.js'
import { useSyncWithProgress } from '@/composables/data/useSyncWithProgress'
import { useLastSynced } from '@/composables/data/useLastSynced'

import ConfirmActionModal from '@/components/common/modals/ConfirmActionModal.vue'
import SyncProgressModal from '@/components/common/modals/SyncProgressModal.vue'
import SyncResultNotification from '@/components/common/feedback/SyncResultNotification.vue'

// ============ SYNC STATUS + FORCE UNLOCK ============

const syncStatusData = ref(null)
const isForceUnlocking = ref(false)
let syncStatusPollId = null

const syncTypeNames = {
  employees: 'Medewerkers',
  'employees-all': 'Volledige medewerkers',
  hours: 'Uren',
  prospects: 'Projecten',
  'prospects-historical': 'Historische saleskansen',
  'ml-training': 'ML model training',
  projects: 'Projecten',
  'timeline-prospects': 'Timeline verrijking',
  services: 'Diensten & facturen',
  all: 'Volledige'
}

const getSyncTypeName = type => syncTypeNames[type] || type

const formatSyncDuration = startedAt => {
  if (!startedAt) return ''
  const ms = Date.now() - new Date(startedAt).getTime()
  const minutes = Math.floor(ms / 60000)
  const seconds = Math.floor((ms % 60000) / 1000)
  if (minutes > 0) return `${minutes} min ${seconds}s`
  return `${seconds}s`
}

const syncStatusSummary = computed(() => {
  if (!syncStatusData.value?.syncs?.length) return ''
  const names = syncStatusData.value.syncs.map(s => getSyncTypeName(s.type))
  return names.join(', ') + ' actief'
})

const pollSyncStatus = async () => {
  try {
    const data = await syncService.getProgress()
    syncStatusData.value = data
  } catch {
    // Ignore polling errors
  }
}

const startSyncStatusPolling = () => {
  if (syncStatusPollId) return
  pollSyncStatus()
  syncStatusPollId = setInterval(pollSyncStatus, 5000)
}

const stopSyncStatusPolling = () => {
  if (syncStatusPollId) {
    clearInterval(syncStatusPollId)
    syncStatusPollId = null
  }
}

// Start polling on mount, stop on unmount
onMounted(() => {
  startSyncStatusPolling()
})

onUnmounted(() => {
  stopSyncStatusPolling()
})

const showForceUnlockConfirm = ref(false)

const handleForceUnlock = () => {
  showForceUnlockConfirm.value = true
}

const confirmForceUnlock = async () => {
  showForceUnlockConfirm.value = false
  isForceUnlocking.value = true
  try {
    const result = await syncService.forceUnlock()
    // Immediately refresh status
    await pollSyncStatus()
    if (result.unlocked > 0) {
      alert(`${result.unlocked} synchronisatie(s) ontgrendeld.`)
    } else {
      alert('Geen actieve synchronisaties gevonden.')
    }
  } catch (err) {
    console.error('Force unlock failed:', err)
    alert('Fout bij ontgrendelen: ' + (err.response?.data?.error || err.message))
  } finally {
    isForceUnlocking.value = false
  }
}

// ============ SYNCHRONISATIE ============

const fullSyncResult = ref(null)

const {
  isModalOpen: isFullSyncModalOpen,
  isSyncing: isFullSyncing,
  progress: fullSyncProgress,
  hasError: fullSyncHasError,
  errorMessage: fullSyncErrorMessage,
  syncResult: _fullSyncResultData,
  startSync: startFullEmployeeSync,
  retrySync: retryFullSync,
  closeModal: closeFullSyncModal
} = useSyncWithProgress('employees-all', () => employeeService.syncAll(), {
  onSuccess: result => {
    fullSyncResult.value = result
  }
})

const { lastSyncedLabel: employeeSyncLabel, lastSyncedFull: employeeSyncFull } = useLastSynced([
  'employees',
  'employees-all'
])

// Timeline prospects sync
const timelineSyncResult = ref(null)

const {
  isModalOpen: isTimelineSyncModalOpen,
  isSyncing: isTimelineSyncing,
  progress: timelineSyncProgress,
  hasError: timelineSyncHasError,
  errorMessage: timelineSyncErrorMessage,
  syncResult: _timelineSyncResultData,
  startSync: startTimelineSync,
  retrySync: retryTimelineSync,
  closeModal: closeTimelineSyncModal
} = useSyncWithProgress('timeline-prospects', () => prospectsLeadsService.syncTimelineProspects(), {
  onSuccess: result => {
    timelineSyncResult.value = result
  }
})

const { lastSyncedLabel: timelineSyncLabel, lastSyncedFull: timelineSyncFull } =
  useLastSynced('timeline-prospects')

// Services & invoices sync
const servicesSyncResult = ref(null)

const {
  isModalOpen: isServicesSyncModalOpen,
  isSyncing: isServicesSyncing,
  progress: servicesSyncProgress,
  hasError: servicesSyncHasError,
  errorMessage: servicesSyncErrorMessage,
  syncResult: _servicesSyncResultData,
  startSync: startServicesSync,
  retrySync: retryServicesSync,
  closeModal: closeServicesSyncModal
} = useSyncWithProgress('services', () => servicesService.syncServices(), {
  onSuccess: result => {
    servicesSyncResult.value = result
  }
})

const { lastSyncedLabel: servicesSyncLabel, lastSyncedFull: servicesSyncFull } =
  useLastSynced('services')
</script>
